import React, { useState } from 'react';
import Meta from 'src/components/Meta';
import { useGaEventPageView } from 'src/utils/gaEvent';
import styled from 'styled-components';
import { ButtonSkeleton, PageCard, PageContainer, Poppins, Settings, Spacer, TableSkeleton } from 'src/common';
import Button from 'src/components/form/Button';
import { ScenarioSelect } from 'src/components/select';
import { ConfirmDeleteModal, IncidentModal } from 'src/components/modals';
import GenericTable, { genEmtyRowTextComponent, GTRowEmpty } from 'src/components/GenericTable';
import { TableExpandContent } from './comps';
import { useIncidentsColumns } from './useIncidentsColumns';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import CommonMenu from 'src/components/CommonMenu';
import { useAuth } from 'src/state/auth';
import { downloadCsv } from './util';
import { AddIncidentModal } from 'src/components/modals/AddIncidentModal/AddIncidentModal';
import { auditEvent } from 'src/api/other';
import { AuditSection } from './sections';
import {
  useDeleteIncidentMutation,
  useIncidentsQuery,
  useLinkScenarioToIncidentsMutation,
} from 'src/graphql/_generated';
import { isQueryLoading, useCache } from 'src/graphql';
import { omit } from 'lodash';

const Div = styled(PageContainer)`
  .panel {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    grid-gap: 10px;

    &__btns {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      grid-gap: 10px;
      flex-wrap: wrap;
      align-self: flex-end;
      justify-self: flex-end;
      margin-left: auto;
    }
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
  }
  .success {
    color: ${({ theme }) => theme.colors.success};
    font-size: 14px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    height: 180px;
    .panel {
      &__btns {
        flex-direction: column;
        align-items: flex-end;
      }
    }
  }
`;

const IncidentsPage = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [addMode, setAddMode] = useState(false);
  const [scenarioId, setScenarioId] = useState('');
  const [status, setStatus] = useState<{ isErr: boolean; text: string | null }>({ isErr: false, text: null });
  const [anchorEl, setAnchorEl] = useState<React.MouseEvent['currentTarget'] | null>(null);
  const { profile } = useAuth();
  useGaEventPageView('Cydea | Security Incidents');
  const { refetchQuery } = useCache();

  const { data, error, loading, networkStatus } = useIncidentsQuery({
    variables: {
      workspaceId: profile?.activeWorkspace?.id,
    },
  });
  const isLoading = isQueryLoading({ loading, data, networkStatus });

  const {
    columns,
    GTColumnSelectAnchorExported,
    setDeleteModalId,
    deleteModalId,
    editData,
    setEditData,
    checked,
    resetChecked,
  } = useIncidentsColumns({
    data: data?.incidents,
    isCheckerEnabled: addMode,
    tdm: { onEditClick: true, onDeleteClick: true },
  });

  const [remove] = useDeleteIncidentMutation({
    variables: { id: deleteModalId! },
    onCompleted: async () => {
      await refetchQuery({ include: ['incidents'] });
      setStatus({ isErr: false, text: '' });
    },
  });

  const [link, { loading: isLinking }] = useLinkScenarioToIncidentsMutation({
    onCompleted: () => {
      setStatus({ isErr: false, text: 'Incidents linked successfully' });
      resetChecked();
      setScenarioId('');
      setAddMode(false);
      refetchQuery({ include: ['incidents', 'incidentLinkedScenarios'] });
    },
    onError: () => setStatus({ isErr: true, text: 'Error linking incidents' }),
  });

  const onMenuClick = (e: React.MouseEvent<HTMLLIElement>, cb: () => void) => {
    e.stopPropagation();
    handleClose();
    cb();
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Meta title={`Cydea | Security Incidents`} feedbackScript />
      {editData && (
        <IncidentModal initialData={omit(editData, ['scenarios', '__typename'])} onClose={() => setEditData(null)} invalidateQueries={['incidents']} />
      )}
      {isCreateModalOpen && <AddIncidentModal onClose={() => setIsCreateModalOpen(false)} invalidateQueries={['incidents']} />}
      {deleteModalId && (
        <ConfirmDeleteModal
          onClose={() => setDeleteModalId(null)}
          onConfirm={async () => {
            setStatus({ isErr: false, text: '' });
            await remove();
            setDeleteModalId(null);
          }}
        />
      )}
      <Div>
        <div className="panel">
          {addMode ? (
            <>
              <Poppins px={22} color="cflowerBlue" css="justify-self: flex-start;margin-right: auto;">
                Select scenario and incidents
              </Poppins>
              <div className="panel__btns">
                <ScenarioSelect value={scenarioId} onChange={(val) => setScenarioId(val.value)} />
                <Button
                  onClick={() => {
                    setAddMode(false);
                    resetChecked();
                    setScenarioId('');
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Cancel',
                      tags: ['INCIDENT'],
                    });
                  }}
                  secondary
                  disabled={isLinking}
                  $constWidth={150}
                >
                  CANCEL
                </Button>
                <Button
                  primary
                  onClick={() => {
                    setStatus({ isErr: false, text: '' });
                    link({ variables: { incidentIds: checked, scenarioId } });
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Link',
                      tags: ['INCIDENT'],
                    });
                  }}
                  disabled={!checked.length || !scenarioId || isLinking}
                  data-cy="link-btn"
                  $constWidth={150}
                >
                  LINK
                </Button>
              </div>
            </>
          ) : (
            <>
              <Poppins px={32} weight={600}>
                Security Incidents
              </Poppins>
              <div className="panel">
                {!isLoading && data?.incidents ? (
                  <>
                    <Button
                      onClick={() => {
                        setAddMode(true);
                        mpEvent(MPEvents.ButtonClick, {
                          button: 'Link to scenario',
                          tags: ['INCIDENT', 'SCENARIO'],
                        });
                      }}
                      primary
                      data-cy="link-scenario-btn"
                    >
                      LINK TO SCENARIO
                    </Button>
                    <Button
                      onClick={() => {
                        setIsCreateModalOpen(true);
                        // setIsModalOpen(true);
                        mpEvent(MPEvents.ButtonClick, {
                          button: 'Add incident',
                          tags: ['INCIDENT', 'SCENARIO'],
                        });
                      }}
                      primary
                      data-cy="add-incident-btn"
                    >
                      + ADD INCIDENT
                    </Button>

                    <>
                      <Settings
                        active={!!anchorEl}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAnchorEl(e.currentTarget);
                        }}
                        data-cy="common-menu"
                        variant="button"
                      />
                      <CommonMenu
                        onCsvClick={(e) =>
                          onMenuClick(e, () => {
                            downloadCsv(data.incidents);
                            auditEvent({
                              action: 'ExportCsv',
                              resource: 'incident',
                              incident_id: data.incidents.map((el) => el.id),
                            });
                          })
                        }
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                      />
                    </>
                  </>
                ) : (
                  <>
                    <ButtonSkeleton />
                    <ButtonSkeleton />
                  </>
                )}
              </div>
            </>
          )}
        </div>
        {status.text && (
          <>
            <Spacer $px={15} />
            <div className={status.isErr ? 'error' : 'success'}>{status.text}</div>
          </>
        )}
        <Spacer $px={30} />
        {isLoading && <TableSkeleton />}
        {error && <div className="error">{error.message}</div>}
        {data?.incidents && (
          <>
            <GenericTable
              tableId={TableIds.incidents}
              data={data?.incidents || []}
              columns={columns}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
              expandContent={(inc) => (inc.scenarios?.length ? <TableExpandContent incidentId={inc.id} /> : null)}
              hideExpandContentWhenClosed
              searchable
              emptyRow={
                <GTRowEmpty
                  text={genEmtyRowTextComponent({
                    onClick: () => setIsCreateModalOpen(true),
                    endText: 'to add incidents.',
                  })}
                />
              }
            />
            <Spacer $px={30} />
            <PageCard
              style={{
                paddingTop: 0,
                paddingBottom: 0,
              }}
            >
              <AuditSection />
            </PageCard>
          </>
        )}
      </Div>
    </>
  );
};

export default IncidentsPage;
